import {
  Button,
  DropdownMenu,
  DropdownMenuItem,
  Icon,
  Placement,
  PlacementArea,
  PlacementManager,
  PlacementReference,
  ProgressSpinner,
  Text,
} from '@bitrise/bitkit';
import { TypeAttribute, TypeMetricKey } from '@trace/types';
import React, { FunctionComponent, useContext, useState } from 'react';

import { ATTRIBUTE_NAME_MAP } from '../../constants/Attributes';
import dataAtIds from '../../dataAtIds.json';
import { trackEvent } from '../../utils/tracking';
import { AttributesContext } from '../Attributes/Attributes';
import { URLStateContext } from '../URLState/URLState';

interface Props {
  disabled?: boolean;
  metric: TypeMetricKey;
}

const AttributeSelector: FunctionComponent<Props> = (props) => {
  const { disabled, metric } = props;
  const { attributeGroups, updateUrlState } = useContext(URLStateContext);
  const { attributes, isEmpty, isFetched, isFetching } = useContext(AttributesContext);
  const [visible, setVisible] = useState(false);
  const attribute = attributeGroups[metric];

  const handleToggleDropdown = () => setVisible(!visible);
  const handleSelect = (attr: TypeAttribute) => {
    trackEvent('Metric.GroupBy', { metric, attribute: attr });
    setVisible(false);
    updateUrlState({
      attributeGroups: {
        ...attributeGroups,
        [metric]: attr,
      },
    });
  };

  const dropdownName = ATTRIBUTE_NAME_MAP[attribute];

  return (
    <PlacementManager>
      <PlacementReference>
        {({ ref }) => (
          <Button
            data-trace-at-id={dataAtIds.AttributeSelectorButton}
            disabled={disabled}
            innerRef={ref}
            level="tertiary"
            onClick={handleToggleDropdown}
            size="small"
          >
            <Text size="x2">{dropdownName}</Text>
            <Icon name="ChevronDown" size="1.25rem" />
          </Button>
        )}
      </PlacementReference>

      <Placement onClose={handleToggleDropdown} visible={visible}>
        {({ width }) => (
          <>
            {isFetched && !isEmpty && (
              <DropdownMenu minWidth={width} withArrow>
                {attributes.map((attr) => (
                  <DropdownMenuItem
                    data-trace-at-id={dataAtIds.AttributeSelectorMenuItem}
                    key={attr}
                    onClick={() => handleSelect(attr)}
                    selected={attribute === attr}
                  >
                    {ATTRIBUTE_NAME_MAP[attr]}
                  </DropdownMenuItem>
                ))}
              </DropdownMenu>
            )}

            {isFetching && (
              <PlacementArea minWidth={width} padding="x6" withArrow>
                <ProgressSpinner />
              </PlacementArea>
            )}

            {isEmpty && (
              <PlacementArea minWidth={width} padding="x6" withArrow>
                There are no attributes to group by.
              </PlacementArea>
            )}
          </>
        )}
      </Placement>
    </PlacementManager>
  );
};

export default AttributeSelector;
